import { AudioManager } from './AudioManager';

/**
 * 手続き型BGM（全てWeb Audio API合成）
 * テンポ: 138BPM、Cマイナー調、軍事アクション風
 *
 * 構成:
 *   - バスドローン（低域持続音）
 *   - リズム（キック + ハイハット）
 *   - ベースライン（音列パターン）
 *   - メロディー（緊張感のある短いフレーズ）
 */
export class BGMPlayer {
  private am: AudioManager;
  private gainNode: GainNode;
  private scheduledIds: ReturnType<typeof setTimeout>[] = [];
  private running = false;
  private loopStartTime = 0;

  // 138 BPM
  private readonly BPM  = 138;
  private readonly BEAT = 60 / this.BPM;
  private readonly BAR  = this.BEAT * 4;
  private readonly LOOP = this.BAR * 8; // 8小節ループ

  // Cマイナー: C2=65.4, D2=73.4, Eb2=77.8, F2=87.3, G2=98, Ab2=103.8, Bb2=116.5
  private readonly NOTES: Record<string, number> = {
    C2: 65.4,  D2: 73.4, Eb2: 77.8, F2: 87.3,
    G2: 98.0, Ab2: 103.8, Bb2: 116.5,
    C3: 130.8, Eb3: 155.6, G3: 196.0, Bb3: 233.1,
    C4: 261.6, F3: 174.6, Ab3: 207.7,
  };

  constructor(am: AudioManager) {
    this.am = am;
    this.gainNode = am.createGain(0.55);
    this.gainNode.connect(am.master);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    // AudioContext が起動してから少し待ってスケジューリング開始
    const startDelay = Math.max(this.am.ctx.currentTime + 0.1, 0.1);
    this.loopStartTime = startDelay;
    this.scheduleLoop(this.loopStartTime);
  }

  stop(): void {
    this.running = false;
    this.scheduledIds.forEach(id => clearTimeout(id));
    this.scheduledIds = [];
    this.gainNode.gain.setTargetAtTime(0, this.am.now, 0.5);
  }

  setVolume(v: number): void {
    this.gainNode.gain.setTargetAtTime(v * 0.55, this.am.now, 0.1);
  }

  private scheduleLoop(startTime: number): void {
    this.scheduleDrone(startTime);
    this.scheduleRhythm(startTime);
    this.scheduleBass(startTime);
    this.scheduleMelody(startTime);

    // 次のループをスケジュール
    const msUntilNext = (startTime + this.LOOP - this.am.ctx.currentTime - 0.1) * 1000;
    const id = setTimeout(() => {
      if (!this.running) return;
      this.loopStartTime = startTime + this.LOOP;
      this.scheduleLoop(this.loopStartTime);
    }, Math.max(0, msUntilNext));
    this.scheduledIds.push(id);
  }

  // ----------------------------------------------------------------
  // ドローン（持続低域）
  // ----------------------------------------------------------------
  private scheduleDrone(t0: number): void {
    this.playPad(t0, this.LOOP, this.NOTES['C2']! * 0.5, 0.12);
    this.playPad(t0, this.LOOP, this.NOTES['G2']! * 0.5, 0.06);
  }

  private playPad(t: number, dur: number, freq: number, vol: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;

    const lp = this.am.createFilter('lowpass', freq * 3, 0.8);
    const g  = this.am.createGain(0);
    osc.connect(lp); lp.connect(g); g.connect(this.gainNode);

    osc.start(t);
    osc.stop(t + dur + 0.1);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.3);
    g.gain.setValueAtTime(vol, t + dur - 0.3);
    g.gain.linearRampToValueAtTime(0, t + dur);
  }

  // ----------------------------------------------------------------
  // リズム（キック + ハイハット）
  // ----------------------------------------------------------------
  private scheduleRhythm(t0: number): void {
    const b = this.BEAT;
    const kickBeats  = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30];
    const hihatBeats = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31];

    for (const beat of kickBeats) {
      this.playKick(t0 + beat * b * 0.5);
    }
    for (const beat of hihatBeats) {
      this.playHihat(t0 + beat * b * 0.5, 0.06);
    }
    // スネア（2,4拍）
    for (const bar of [0, 1, 2, 3, 4, 5, 6, 7]) {
      this.playSnare(t0 + bar * this.BAR + b);
      this.playSnare(t0 + bar * this.BAR + b * 3);
    }
  }

  private playKick(t: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(30, t + 0.12);

    const g = this.am.createGain(0);
    osc.connect(g); g.connect(this.gainNode);
    osc.start(t); osc.stop(t + 0.2);
    g.gain.setValueAtTime(0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
  }

  private playSnare(t: number): void {
    const ctx = this.am.ctx;
    const buf = this.am.createNoiseBuffer(0.15);
    const ns  = ctx.createBufferSource();
    ns.buffer = buf;

    const hp = this.am.createFilter('highpass', 2000, 1);
    const g  = this.am.createGain(0);
    ns.connect(hp); hp.connect(g); g.connect(this.gainNode);
    ns.start(t); ns.stop(t + 0.15);
    g.gain.setValueAtTime(0.2, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
  }

  private playHihat(t: number, vol: number): void {
    const ctx = this.am.ctx;
    const buf = this.am.createNoiseBuffer(0.05);
    const ns  = ctx.createBufferSource();
    ns.buffer = buf;

    const hp = this.am.createFilter('highpass', 8000, 1);
    const g  = this.am.createGain(0);
    ns.connect(hp); hp.connect(g); g.connect(this.gainNode);
    ns.start(t); ns.stop(t + 0.06);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
  }

  // ----------------------------------------------------------------
  // ベースライン
  // ----------------------------------------------------------------
  private scheduleBass(t0: number): void {
    const b = this.BEAT;
    // 8小節パターン（Cマイナーペンタトニック）
    const pattern: [number, string, number][] = [
      // [拍オフセット, 音名, 長さ(拍)]
      [0,   'C2',  1], [1,  'C2',  0.5], [1.5, 'Eb2', 0.5],
      [2,   'G2',  1], [3,  'F2',  0.5], [3.5, 'Eb2', 0.5],
      [4,   'C2',  1], [5,  'C2',  0.5], [5.5, 'D2',  0.5],
      [6,   'Eb2', 1], [7,  'G2',  1],
      // bar 2-4: 繰り返し変形
      [8,   'C2',  1], [9,  'Eb2', 1], [10, 'G2',  1], [11, 'F2',  1],
      [12,  'Bb2', 1], [13, 'Ab2', 1], [14, 'G2',  1], [15, 'C2',  1],
      // bar 5-8
      [16,  'C2',  1], [17, 'C2',  0.5],[17.5,'Eb2',0.5],
      [18,  'G2',  1], [19, 'G2',  1],
      [20,  'F2',  1], [21, 'Eb2', 1], [22, 'D2',  1], [23, 'C2',  1],
      [24,  'Bb2', 1], [25, 'C3',  1], [26, 'Bb2', 1], [27, 'Ab2', 1],
      [28,  'G2',  2], [30, 'F2',  1], [31, 'C2',  1],
    ];

    for (const [beat, note, len] of pattern) {
      const freq = this.NOTES[note];
      if (!freq) continue;
      this.playBassNote(t0 + beat * b, len * b * 0.9, freq);
    }
  }

  private playBassNote(t: number, dur: number, freq: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = freq;

    const lp = this.am.createFilter('lowpass', freq * 4, 1.5);
    const g  = this.am.createGain(0);
    osc.connect(lp); lp.connect(g); g.connect(this.gainNode);
    osc.start(t); osc.stop(t + dur + 0.05);
    g.gain.setValueAtTime(0.28, t);
    g.gain.setValueAtTime(0.28, t + dur * 0.7);
    g.gain.linearRampToValueAtTime(0, t + dur);
  }

  // ----------------------------------------------------------------
  // メロディー（2音域上で緊張感のあるフレーズ）
  // ----------------------------------------------------------------
  private scheduleMelody(t0: number): void {
    const b = this.BEAT;
    // 高域メロディー（bar 3〜4 と 7〜8 に出現）
    const phrases: [number, string, number][] = [
      [8,   'G3',  0.5], [8.5, 'Ab3', 0.5], [9,  'Bb3', 1],
      [10,  'G3',  0.5], [10.5,'F3',  0.5], [11, 'Eb3', 1],
      [12,  'C3',  1.5], [13.5,'D2',  0.5],
      [14,  'Eb3', 0.5], [14.5,'F3',  0.5], [15, 'G3',  1],

      [24,  'C4',  0.5], [24.5,'Bb3', 0.5], [25, 'Ab3', 1],
      [26,  'G3',  0.5], [26.5,'F3',  0.5], [27, 'Eb3', 1],
      [28,  'G3',  1],   [29,  'F3',  1],
      [30,  'Eb3', 1],   [31,  'C3',  1],
    ];

    for (const [beat, note, len] of phrases) {
      const freq = this.NOTES[note];
      if (!freq) continue;
      this.playMelodyNote(t0 + beat * b, len * b * 0.85, freq);
    }
  }

  private playMelodyNote(t: number, dur: number, freq: number): void {
    const ctx = this.am.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = freq;

    const lp = this.am.createFilter('lowpass', freq * 6, 1);
    const g  = this.am.createGain(0);
    osc.connect(lp); lp.connect(g); g.connect(this.gainNode);
    osc.start(t); osc.stop(t + dur + 0.05);
    g.gain.setValueAtTime(0.12, t);
    g.gain.setValueAtTime(0.10, t + dur * 0.6);
    g.gain.linearRampToValueAtTime(0, t + dur);
  }
}
